import { Typography, Tooltip, Dropdown, Tag } from "antd";
import { MoreOutlined } from "@ant-design/icons";
import CustomButton from "../../../components/CustomButton/Button";
import { BTN_TXT, GLOBAL_CONSTANTS, FINANCING_CATEGORY_COLUMN_FILTERS } from "../../../constants/constants";
import { PERM_CODE } from "../../../constants/PermissionCodes";
import { formatCurrency } from "../../../helpers/helpers";
import { safeStringSort, safeNumberSort, safeDateSort } from "../../../helpers/sortHelpers";
import dayjs from "dayjs";

const { Text } = Typography;

export const getCreditLimitRequestsColumns = ({
  onApprove,
  onReject,
  onReopen,
  onNavigateEnterprise,
  onViewActivityLog,
  privileges = [],
}) => {
  const columns = [
    {
      title: "Business Name",
      dataIndex: "orgName",
      key: "orgName",
      width: 200,
      sorter: (a, b) => safeStringSort(a?.orgName, b?.orgName),
      render: (text, record) => text || record.orgId || GLOBAL_CONSTANTS.NA,
    },
    {
      title: "Enterprise Name",
      dataIndex: "enterpriseName",
      key: "enterpriseName",
      width: 200,
      sorter: (a, b) => safeStringSort(a?.enterpriseName, b?.enterpriseName),
      render: (text) => text || GLOBAL_CONSTANTS.NA,
    },
    {
      title: "Enterprise Status",
      dataIndex: "isAvailableInSystem",
      key: "isAvailableInSystem",
      width: 160,
      sorter: (a, b) => safeNumberSort(a?.isAvailableInSystem, b?.isAvailableInSystem),
      render: (isAvailable) => (
        <Tag color={isAvailable ? "success" : "warning"}>
          {isAvailable ? "Active in System" : "New Enterprise"}
        </Tag>
      ),
    },

    {
      title: "Financing Type",
      dataIndex: "financingType",
      key: "financingType",
      width: 180,
      filters: FINANCING_CATEGORY_COLUMN_FILTERS,
      onFilter: (value, record) => record?.dealCategoryName === value,
      sorter: (a, b) => safeStringSort(a?.financingType, b?.financingType),
      render: (type, record) => {
        const financingType =
          type ||
          (record?.dealCategoryId === "DCT1000006"
            ? "Supply Chain Financing"
            : "Invoice Financing");

        const normalized = financingType.toLowerCase();

        if (normalized.includes("invoice")) {
          return <Tag color="blue">{financingType}</Tag>;
        }

        if (normalized.includes("supply chain")) {
          return <Tag color="purple">{financingType}</Tag>;
        }

        return <Tag color="default">{financingType}</Tag>;
      },
    },

    {
      title: "Requested Credit",
      dataIndex: "requestedCredit",
      key: "requestedCredit",
      width: 180,
      sorter: (a, b) => safeNumberSort(a?.requestedCredit, b?.requestedCredit),
      render: (text) => (text ? formatCurrency(text) : GLOBAL_CONSTANTS.NA),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 150,
      sorter: (a, b) => safeStringSort(a?.status, b?.status),
      render: (status, record) => {
        if (status === "Approved") {
          return <Tag color="success">Approved</Tag>;
        }
        if (status === "Rejected") {
          return record.rejectionReason ? (
            <Tooltip title={`Reason: ${record.rejectionReason}`}>
              <Tag color="error">Rejected</Tag>
            </Tooltip>
          ) : (
            <Tag color="error">Rejected</Tag>
          );
        }
        return <Tag color="warning">Pending</Tag>;
      },
    },
    {
      title: "Requested Date",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 150,
      sorter: (a, b) => safeDateSort(a?.createdAt, b?.createdAt),
      render: (date) =>
        date ? dayjs(date).format("DD/MM/YYYY") : GLOBAL_CONSTANTS.NA,
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      fixed: "right",
      render: (_, record) => {
        const items = [];
        const privilegeCodes = (privileges || [])
          .map((p) => (typeof p === "string" ? p : p?.privilegeCode))
          .filter(Boolean);
        const isPendingRequest =
          record.status === "Pending Credit Limit" ||
          record.status === "Pending";

        if (isPendingRequest) {
          if (record.isAvailableInSystem) {
            if (privilegeCodes.includes(PERM_CODE.CPSAC)) {
              items.push({
                key: "approve",
                label: BTN_TXT.APPROVE,
              });
            }
          } else {
            items.push({
              key: "onboard",
              label: "Onboard Enterprise",
            });
          }

          // Reject must be available for Pending credit-limit requests (CPSRE)
          if (privilegeCodes.includes(PERM_CODE.CPSRE)) {
            items.push({
              key: "reject",
              label: BTN_TXT.REJECT,
            });
          }
        }

        if (
          record.status === "Rejected" &&
          privileges.includes(PERM_CODE.CRSRO)
        ) {
          items.push({
            key: "reopen",
            label: BTN_TXT.REOPEN,
          });
        }

        items.push({
          key: "activityLog",
          label: "Activity Log",
        });

        if (!items.length) {
          return <Text type="secondary">-</Text>;
        }

        const handleMenuClick = ({ key }) => {
          switch (key) {
            case "approve":
              onApprove?.(record);
              break;
            case "reject":
              onReject?.(record);
              break;
            case "reopen":
              onReopen?.(record);
              break;
            case "onboard":
              onNavigateEnterprise?.(record);
              break;
            case "activityLog":
              onViewActivityLog?.(record);
              break;
            default:
              break;
          }
        };

        return (
          <Dropdown
            trigger={["click"]}
            placement="bottomRight"
            menu={{
              items,
              onClick: handleMenuClick,
            }}
          >
            <CustomButton
              type="text"
              icon={<MoreOutlined style={{ fontSize: 18 }} />}
            />
          </Dropdown>
        );
      },
    },
  ];

  return columns;
};
